import React, { useEffect, useMemo, useState } from "react";
import { Formik, Form, Field } from "formik";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import classNames from "classnames";
import OutsideClickHandler from "react-outside-click-handler";
import {
  Check,
  ChevronDown,
  Clock,
  MessageSquare,
  Paperclip,
  Send,
  User,
} from "lucide-react";
import Modal from "../components/Modal/Modal";
import Input from "../components/Input/Input";
import Textarea from "../components/Textarea/Textarea";
import Button from "../components/Button/Button";
import SecondaryButton from "../components/Button/SecondaryButton";
import RaciAssignment from "../components/RaciAssignment/RaciAssignment";
import CascadingDropdown from "../components/CascadingDropdown/CascadingDropdown";
import FormikUpload from "../components/FormikComponents/FormikUpload/FormikUpload";
import ConfirmationPopup from "./ConfirmationPopup";
import WarningIllustration from "../icons/no-data.svg";
import { hideModal } from "../redux/modalStore";
import axiosInstance from "../utils/utils";
import { getOrganizationData } from "../actions/organization";
import detailsStyles from "../views/FindingDetails/FindingDetails.module.scss";
import styles from "./FindingsModal.module.scss";

const statusOptions = [
  { id: "Open", label: "Open", tone: "open" },
  { id: "In Progress", label: "In Progress", tone: "progress" },
  { id: "In Review", label: "In Review", tone: "review" },
  { id: "Done", label: "Done", tone: "done" },
];

const normalizeArray = (value) => {
  if (Array.isArray(value)) return value;
  if (value) return [value];
  return [];
};

const normalizeAttachments = (attachments = []) =>
  attachments
    .map((item) =>
      typeof item === "string" ? item : item?.url || item?.link || "",
    )
    .filter(Boolean);

const formatDateTime = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
};

const getRelativeTime = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const diffMs = Date.now() - date.getTime();
  const diffSec = Math.round(diffMs / 1000);
  if (diffSec < 45) return "just now";
  const diffMin = Math.round(diffSec / 60);
  if (diffMin < 60) return `${diffMin} minute${diffMin === 1 ? "" : "s"} ago`;
  const diffHours = Math.round(diffMin / 60);
  if (diffHours < 24) {
    return `about ${diffHours} hour${diffHours === 1 ? "" : "s"} ago`;
  }
  const diffDays = Math.round(diffHours / 24);
  if (diffDays < 7) return `${diffDays} day${diffDays === 1 ? "" : "s"} ago`;
  const diffWeeks = Math.round(diffDays / 7);
  if (diffWeeks < 5) return `${diffWeeks} week${diffWeeks === 1 ? "" : "s"} ago`;
  const diffMonths = Math.round(diffDays / 30);
  if (diffMonths < 12) return `${diffMonths} month${diffMonths === 1 ? "" : "s"} ago`;
  const diffYears = Math.round(diffDays / 365);
  return `${diffYears} year${diffYears === 1 ? "" : "s"} ago`;
};

const formatFileSize = (bytes) => {
  if (!bytes || Number.isNaN(bytes)) return "";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let size = Number(bytes);
  let unitIndex = 0;
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex += 1;
  }
  return `${size.toFixed(size < 10 && unitIndex > 0 ? 1 : 0)} ${units[unitIndex]}`;
};

const parseUpdateBody = (body) => {
  if (!body) return [];
  const rawText = typeof body === "string" ? body : String(body);
  const lines = rawText
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => (line.startsWith("- ") ? line.slice(2).trim() : line));

  return lines.map((line) => {
    const colonIndex = line.indexOf(":");
    if (colonIndex > 0) {
      const label = line.slice(0, colonIndex).trim();
      const value = line.slice(colonIndex + 1).trim();
      if (label) {
        return { label, value };
      }
    }
    return { text: line };
  });
};

const getUpdateTimestamp = (update) =>
  update?.date ||
  update?.updated_at ||
  update?.updatedAt ||
  update?.updated_on ||
  update?.updatedOn ||
  update?.created_at ||
  update?.createdAt ||
  update?.created_on ||
  update?.createdOn;

const getUpdateBodyText = (update) => {
  if (!update?.body) return "";
  return typeof update.body === "string" ? update.body : String(update.body);
};

const getUpdateAuthor = (update) => {
  const candidate =
    update?.author || update?.user || update?.created_by || update?.createdBy;
  if (typeof candidate === "string") return candidate;
  if (candidate && typeof candidate === "object") {
    return candidate.email || candidate.name || "";
  }
  return "";
};

const normalizeUpdateAttachments = (attachments = []) => {
  const items = Array.isArray(attachments) ? attachments : [attachments];
  return items
    .map((attachment) => {
      if (!attachment) return null;
      if (typeof attachment === "string") {
        return {
          url: attachment,
          filename: attachment.split("/").pop() || "Attachment",
        };
      }
      const url = attachment.url || attachment.path || attachment.link || "";
      const filename =
        attachment.filename ||
        attachment.name ||
        (url ? url.split("/").pop() : "") ||
        "Attachment";
      return { url, filename };
    })
    .filter((attachment) => attachment && attachment.url);
};

const isStructuredUpdate = (update) => {
  const body = getUpdateBodyText(update).trim();
  if (!body) return false;
  return parseUpdateBody(body).some((item) => item.label);
};

const isCommentUpdate = (update) => {
  const body = getUpdateBodyText(update).trim();
  if (body) return !isStructuredUpdate(update);
  return normalizeUpdateAttachments(update?.attachments || []).length > 0;
};

const FindingsModal = () => {
  const dispatch = useDispatch();
  const { orgId } = useParams();

  const openedModal = useSelector((state) => state.modal.openedModal);
  const { findingsData, risksData } = useSelector(
    (state) => state.organization,
  );
  const userEmail = useSelector((state) => state.user.email);

  const sessionToken = localStorage.getItem("session_token");

  const findingKey = openedModal?.data?.findingKey;
  const finding = useMemo(() => {
    if (openedModal?.name !== "findings_modal") return null;
    if (findingKey) {
      const directMatch = findingsData?.[findingKey];
      if (directMatch) return directMatch;
      const fallbackMatch = Object.values(findingsData || {}).find(
        (item) => item.key === findingKey,
      );
      if (fallbackMatch) return fallbackMatch;
    }
    return openedModal?.data?.finding || null;
  }, [openedModal?.name, openedModal?.data?.finding, findingKey, findingsData]);

  const [activeTab, setActiveTab] = useState("details");
  const [isStatusOpen, setIsStatusOpen] = useState(false);
  const [showConfirmationPopup, setShowConfirmationPopup] = useState(false);
  const [updates, setUpdates] = useState([]);

  useEffect(() => {
    if (openedModal?.name === "findings_modal") {
      setActiveTab("details");
      setIsStatusOpen(false);
    }
  }, [openedModal?.name, findingKey]);

  useEffect(() => {
    const nextUpdates = Array.isArray(finding?.updates) ? finding.updates : [];
    setUpdates(nextUpdates);
  }, [finding?.updates]);

  const sortedRisks = useMemo(() => {
    const risksArray = Object.values(risksData || {});
    return [...risksArray].sort((a, b) =>
      String(a.risk_id || "").localeCompare(String(b.risk_id || "")),
    );
  }, [risksData]);

  const cascadingRiskOptions = useMemo(
    () =>
      sortedRisks
        .filter((risk) => risk.level?.toLowerCase() !== "none")
        .map((risk) => ({
          id: risk.risk_num,
          reference: risk.risk_id,
          label: `${risk.risk_id} ${risk.description}`,
        })),
    [sortedRisks],
  );

  const cascadingChapterOptions = useMemo(() => {
    const validChapterIds = new Set(
      cascadingRiskOptions
        .map((risk) => String(risk.reference || "").split("-")[0])
        .filter(Boolean),
    );

    return Array.from(validChapterIds)
      .map((chapterId) => {
        const matchingRisk = sortedRisks.find(
          (risk) => String(risk.qms_id) === chapterId,
        );
        return {
          id: chapterId,
          label: `${chapterId} ${matchingRisk?.qms_description || ""}`,
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [cascadingRiskOptions, sortedRisks]);

  const handleClose = (dirty, resetForm) => {
    if (dirty) {
      setShowConfirmationPopup(true);
      return;
    }
    if (resetForm) {
      resetForm();
    }
    dispatch(hideModal());
  };

  const handleConfirmClose = (resetForm) => {
    setShowConfirmationPopup(false);
    if (resetForm) {
      resetForm();
    }
    dispatch(hideModal());
  };

  const handleCancelClose = () => {
    setShowConfirmationPopup(false);
  };

  if (!finding) {
    return null;
  }

  const existingAttachments = finding.attachments || [];
  const initialValues = {
    reference: finding.reference || "",
    description: finding.description || "",
    responsible: Array.isArray(finding.responsible)
      ? finding.responsible.join(", ")
      : finding.responsible || "",
    accountable: normalizeArray(finding.accountable),
    consulted: normalizeArray(finding.consulted),
    informed: normalizeArray(finding.informed),
    risk: finding.risk?.risk_num ? String(finding.risk.risk_num) : "",
    attachments: normalizeAttachments(existingAttachments),
    status: finding.status || "Open",
  };

  const handleSave = async (values, formikHelpers) => {
    const selectedRisk = cascadingRiskOptions.find(
      (opt) => String(opt.id) === String(values.risk),
    );
    const payload = {};

    const originalAttachments = normalizeAttachments(existingAttachments);
    const cleanedResponsible = values.responsible?.trim?.() || "";

    if ((finding.reference || "") !== values.reference) {
      payload.reference = values.reference;
    }
    if ((finding.description || "") !== values.description) {
      payload.description = values.description;
    }
    if ((finding.status || "") !== values.status) {
      payload.status = values.status;
    }

    const currentResponsible = Array.isArray(finding.responsible)
      ? finding.responsible[0] || ""
      : finding.responsible || "";
    if (currentResponsible !== cleanedResponsible) {
      payload.responsible = cleanedResponsible;
    }

    ["accountable", "consulted", "informed"].forEach((field) => {
      const current = normalizeArray(finding[field]);
      const next = normalizeArray(values[field])
        .map((entry) => (typeof entry === "string" ? entry.trim() : ""))
        .filter(Boolean);
      if (JSON.stringify(current) !== JSON.stringify(next)) {
        payload[field] = next;
      }
    });

    const nextAttachments = values.attachments || [];
    if (JSON.stringify(originalAttachments) !== JSON.stringify(nextAttachments)) {
      payload.attachments = nextAttachments;
    }

    if (selectedRisk) {
      const currentRisk = finding.risk?.risk_num || "";
      if (String(currentRisk) !== String(selectedRisk.id || "")) {
        payload.risk = String(selectedRisk.id);
      }
    }

    if (Object.keys(payload).length === 0) {
      formikHelpers.setSubmitting(false);
      return;
    }

    const requestBody = [
      {
        id: finding.id,
        ...payload,
      },
    ];

    try {
      await axiosInstance.patch(
        `/nocodb/findings?org_id=${orgId}`,
        requestBody,
        {
          headers: {
            "Content-Type": "application/json",
            "session-token": sessionToken,
          },
        },
      );

      await dispatch(
        getOrganizationData(
          orgId,
          sessionToken,
          null,
          false,
          true,
          true,
          true,
          false,
          false,
        ),
      );
    } catch (error) {
      console.error("Failed to save finding", error);
    } finally {
      formikHelpers.setSubmitting(false);
    }
  };

  const handleUpdateSubmit = async (
    values,
    { resetForm, setFieldValue, setSubmitting },
  ) => {
    try {
      const requestBody = {
        body: values.body,
        attachments: (values.attachments || []).map((attachment) => attachment),
        finding: finding?.id || 0,
        improvement: 0,
        audit_task: 0,
      };

      const response = await axiosInstance.post(
        `/nocodb/update?org_id=${orgId}`,
        requestBody,
        {
          headers: {
            "session-token": sessionToken,
            "Content-Type": "application/json",
          },
        },
      );

      if (response.status === 200) {
        const transformedAttachments = normalizeUpdateAttachments(
          values.attachments || [],
        );
        setUpdates((prevUpdates) => [
          {
            author: userEmail,
            date: new Date().toISOString(),
            body: values.body,
            attachments: transformedAttachments,
          },
          ...prevUpdates,
        ]);
        setFieldValue("attachments", []);

        await dispatch(
          getOrganizationData(
            orgId,
            sessionToken,
            null,
            false,
            true,
            true,
            true,
            false,
            false,
          ),
        );

        resetForm();
      }
    } catch (error) {
      console.error("Error adding update:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const lastUpdatedAt = updates.reduce((latest, update) => {
    const candidate = getUpdateTimestamp(update);
    if (!candidate) return latest;
    if (!latest) return candidate;
    const candidateTime = new Date(candidate).getTime();
    const latestTime = new Date(latest).getTime();
    if (Number.isNaN(candidateTime)) return latest;
    if (Number.isNaN(latestTime) || candidateTime > latestTime) return candidate;
    return latest;
  }, "");

  const initialHoveredOption =
    finding.risk?.qms_id ||
    (finding.risk?.risk_id
      ? String(finding.risk.risk_id).split("-")[0]
      : null);

  const renderTabContent = (tab, values) => {
    if (tab === "attachments") {
      return (
        <div className={detailsStyles.attachmentsTab}>
          <div className={detailsStyles.attachmentsHeading}>Attachments</div>
          {existingAttachments.length > 0 ? (
            <div className={detailsStyles.existingAttachments}>
              {existingAttachments.map((attachment, index) => {
                const url =
                  attachment.url || attachment.link || attachment.path || attachment;
                const label =
                  attachment.filename ||
                  attachment.name ||
                  (typeof attachment === "string"
                    ? attachment.split("/").pop()
                    : "Attachment");
                const sizeValue =
                  attachment.size ||
                  attachment.file_size ||
                  attachment.filesize ||
                  attachment.fileSize;
                const sizeLabel = sizeValue ? formatFileSize(sizeValue) : "";

                return (
                  <a
                    key={`${url}-${index}`}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={detailsStyles.existingAttachmentItem}
                  >
                    <div className={detailsStyles.existingAttachmentInfo}>
                      <Paperclip
                        size={16}
                        className={detailsStyles.existingAttachmentIcon}
                      />
                      <div className={detailsStyles.existingAttachmentText}>
                        <div className={detailsStyles.existingAttachmentName}>
                          {label}
                        </div>
                        {sizeLabel && (
                          <div className={detailsStyles.existingAttachmentSize}>
                            {sizeLabel}
                          </div>
                        )}
                      </div>
                    </div>
                  </a>
                );
              })}
            </div>
          ) : (
            <div className={detailsStyles.emptyState}>
              No attachments uploaded yet.
            </div>
          )}
        </div>
      );
    }

    if (tab === "comments") {
      const commentEntries = updates
        .filter(isCommentUpdate)
        .map((update) => {
          const attachments = normalizeUpdateAttachments(
            update?.attachments || [],
          );
          return {
            date: getUpdateTimestamp(update),
            author: getUpdateAuthor(update),
            body: getUpdateBodyText(update).trim(),
            attachments,
          };
        })
        .filter((entry) => entry.body || entry.attachments.length)
        .sort((a, b) => {
          const dateA = a.date ? new Date(a.date).getTime() : 0;
          const dateB = b.date ? new Date(b.date).getTime() : 0;
          if (!dateA && !dateB) return 0;
          if (!dateA) return 1;
          if (!dateB) return -1;
          return dateB - dateA;
        });

      if (!commentEntries.length) {
        return <div className={detailsStyles.emptyState}>No comments yet.</div>;
      }

      return (
        <div className={detailsStyles.commentsCard}>
          <div className={detailsStyles.commentsHeader}>
            <div className={detailsStyles.commentsTitleRow}>
              <MessageSquare size={20} />
              <div className={detailsStyles.commentsTitle}>Comments</div>
            </div>
            <div className={detailsStyles.commentsSubtitle}>
              Notes and attachments added by the team
            </div>
          </div>
          <div className={detailsStyles.commentsList}>
            {commentEntries.map((entry, idx) => {
              const relative = entry.date ? getRelativeTime(entry.date) : "";
              const timestamp = entry.date ? formatDateTime(entry.date) : "";
              const author = entry.author || "Unknown";

              return (
                <div
                  key={`${author}-${entry.date || idx}`}
                  className={detailsStyles.commentItem}
                >
                  <div className={detailsStyles.commentMetaRow}>
                    <div className={detailsStyles.commentAuthor}>{author}</div>
                    {relative && (
                      <div className={detailsStyles.commentTime}>{relative}</div>
                    )}
                  </div>
                  {timestamp && (
                    <div className={detailsStyles.commentTimestamp}>{timestamp}</div>
                  )}
                  {entry.body && (
                    <div className={detailsStyles.commentBody}>{entry.body}</div>
                  )}
                  {entry.attachments.length > 0 && (
                    <div className={detailsStyles.commentAttachments}>
                      {entry.attachments.map((attachment, attachmentIndex) => (
                        <a
                          key={`${attachment.url}-${attachmentIndex}`}
                          href={attachment.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={detailsStyles.commentAttachment}
                        >
                          <Paperclip size={14} />
                          <span>{attachment.filename}</span>
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      );
    }

    if (tab === "change-history") {
      const historyEntries = updates
        .filter(isStructuredUpdate)
        .map((update) => ({
          type: "Updated",
          date: getUpdateTimestamp(update),
          author: getUpdateAuthor(update),
          body: getUpdateBodyText(update) || "Updated record",
        }));

      const sortedEntries = historyEntries
        .filter((entry) => entry.date || entry.author || entry.body)
        .sort((a, b) => {
          const dateA = a.date ? new Date(a.date).getTime() : 0;
          const dateB = b.date ? new Date(b.date).getTime() : 0;
          if (!dateA && !dateB) return 0;
          if (!dateA) return 1;
          if (!dateB) return -1;
          return dateB - dateA;
        });

      if (!sortedEntries.length) {
        return <div className={detailsStyles.emptyState}>No changes yet.</div>;
      }

      return (
        <div className={detailsStyles.changeHistoryCard}>
          <div className={detailsStyles.changeHistoryHeader}>
            <div className={detailsStyles.changeHistoryTitleRow}>
              <Clock size={20} />
              <div className={detailsStyles.changeHistoryTitle}>Change History</div>
            </div>
            <div className={detailsStyles.changeHistorySubtitle}>
              Track of all changes made to this record
            </div>
          </div>
          <div className={detailsStyles.changeHistoryList}>
            {sortedEntries.map((entry, idx) => {
              const relative = entry.date ? getRelativeTime(entry.date) : "";
              const timestamp = entry.date ? formatDateTime(entry.date) : "";
              const rawBody =
                entry.body && typeof entry.body !== "string"
                  ? String(entry.body)
                  : entry.body || "";
              const parsedBody = rawBody ? parseUpdateBody(rawBody) : [];
              const hasStructuredBody =
                parsedBody.length > 1 || parsedBody.some((item) => item.label);

              return (
                <div
                  key={`${entry.type}-${idx}`}
                  className={classNames(
                    detailsStyles.changeHistoryItem,
                    detailsStyles.historyUpdated,
                  )}
                >
                  <div className={detailsStyles.changeHistoryMetaRow}>
                    <div className={detailsStyles.changeHistoryMetaLeft}>
                      <div
                        className={classNames(
                          detailsStyles.changeHistoryBadge,
                          detailsStyles.badgeUpdated,
                        )}
                      >
                        {entry.type}
                      </div>
                      {relative && (
                        <span className={detailsStyles.changeHistoryRelative}>
                          {relative}
                        </span>
                      )}
                    </div>
                    {timestamp && (
                      <span className={detailsStyles.changeHistoryTimestamp}>
                        {timestamp}
                      </span>
                    )}
                  </div>
                  {(entry.author || entry.body) && (
                    <div className={detailsStyles.changeHistoryDetails}>
                      {entry.author && (
                        <div className={detailsStyles.changeHistoryAuthor}>
                          <User size={14} />
                          <span>{entry.author}</span>
                        </div>
                      )}
                      {entry.body && (
                        <div className={detailsStyles.changeHistoryBody}>
                          {!hasStructuredBody ? (
                            <span className={detailsStyles.changeHistoryBodyText}>
                              {rawBody}
                            </span>
                          ) : (
                            <ul className={detailsStyles.changeHistoryBodyList}>
                              {parsedBody.map((item, itemIndex) => (
                                <li
                                  key={`${entry.type}-${idx}-${itemIndex}`}
                                  className={detailsStyles.changeHistoryBodyItem}
                                >
                                  {item.label ? (
                                    <>
                                      <span
                                        className={detailsStyles.changeHistoryBodyLabel}
                                      >
                                        {item.label}
                                      </span>
                                      <span
                                        className={detailsStyles.changeHistoryBodyValue}
                                      >
                                        {item.value || "Not set"}
                                      </span>
                                    </>
                                  ) : (
                                    <span
                                      className={detailsStyles.changeHistoryBodyText}
                                    >
                                      {item.text}
                                    </span>
                                  )}
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      );
    }

    return (
      <div className={detailsStyles.detailsGrid}>
        <div className={detailsStyles.fieldBlock}>
          <label htmlFor="reference">Reference</label>
          <Field name="reference" component={Input} placeholder="Reference" />
        </div>

        <div className={classNames(detailsStyles.fieldBlock, detailsStyles.fullWidth)}>
          <label htmlFor="description">Description</label>
          <Field
            name="description"
            component={Textarea}
            placeholder="Description"
          />
        </div>

        <div className={classNames(detailsStyles.fieldBlock, detailsStyles.fullWidth)}>
          <Field
            name="risk"
            component={CascadingDropdown}
            label="Related Risk Profile"
            text="Search and select risk profile..."
            options={cascadingChapterOptions}
            suboptions={cascadingRiskOptions}
            selectedValues={[values?.risk]}
            initialHoveredOption={initialHoveredOption}
            className={detailsStyles.riskDropdown}
            openDirection="up"
          />
        </div>
      </div>
    );
  };

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      onSubmit={(values, helpers) => {
        helpers.setSubmitting(true);
        handleSave(values, helpers);
      }}
    >
      {({ values, dirty, isSubmitting, submitForm, resetForm, setFieldValue }) => {
        const statusTone =
          statusOptions.find((option) => option.id === values.status)?.tone ||
          "open";

        return (
          <>
            <Modal
              name="findings_modal"
              onClose={() => handleClose(dirty, resetForm)}
              width="70vw"
              maxWidth="1180px"
            >
              <div className={classNames(detailsStyles.page, styles.modalPage)}>
                <Form className={detailsStyles.form}>
                  <div className={styles.headerRow}>
                    <div className={styles.metaColumn}>
                      <div className={detailsStyles.keyPill}>{finding.key}</div>
                      <div className={styles.statusBlock}>
                        <div className={detailsStyles.statusLabel}>Status</div>
                        <OutsideClickHandler
                          onOutsideClick={() => setIsStatusOpen(false)}
                          disabled={!isStatusOpen}
                        >
                          <div className={detailsStyles.statusDropdown}>
                            <button
                              type="button"
                              className={classNames(
                                detailsStyles.statusSelect,
                                detailsStyles[`status-${statusTone}`],
                              )}
                              onClick={() =>
                                setIsStatusOpen((prev) => !prev)
                              }
                            >
                              <span>{values.status}</span>
                              <ChevronDown size={16} />
                            </button>
                            {isStatusOpen && (
                              <div className={detailsStyles.statusMenu}>
                                {statusOptions.map((option) => (
                                  <button
                                    key={option.id}
                                    type="button"
                                    className={classNames(detailsStyles.statusOption, {
                                      [detailsStyles.activeStatus]:
                                        values.status === option.id,
                                    })}
                                    onClick={() => {
                                      setFieldValue("status", option.id);
                                      setIsStatusOpen(false);
                                    }}
                                  >
                                    <span className={detailsStyles.statusCheckSlot}>
                                      {values.status === option.id && (
                                        <Check size={14} />
                                      )}
                                    </span>
                                    <div
                                      className={classNames(
                                        detailsStyles.statusDot,
                                        detailsStyles[`dot-${option.tone}`],
                                      )}
                                    />
                                    <span className={detailsStyles.statusOptionLabel}>
                                      {option.label}
                                    </span>
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        </OutsideClickHandler>
                      </div>
                      {lastUpdatedAt && (
                        <div className={styles.lastUpdated}>
                          <div className={detailsStyles.metaLabel}>
                            Last updated
                          </div>
                          <div className={detailsStyles.metaValue}>
                            {formatDateTime(lastUpdatedAt)}
                          </div>
                        </div>
                      )}
                    </div>
                    <div className={styles.raciColumn}>
                      <div className={detailsStyles.sectionLabel}>
                        RACI assignment
                      </div>
                      <RaciAssignment />
                    </div>
                  </div>

                  <div className={detailsStyles.bodyGrid}>
                    <div>
                      <div className={detailsStyles.tabHeaderRow}>
                        <div className={detailsStyles.tabs}>
                          <button
                            type="button"
                            className={classNames(detailsStyles.tabButton, {
                              [detailsStyles.activeTab]: activeTab === "details",
                            })}
                            onClick={() => setActiveTab("details")}
                          >
                            Details
                          </button>
                          <button
                            type="button"
                            className={classNames(detailsStyles.tabButton, {
                              [detailsStyles.activeTab]:
                                activeTab === "attachments",
                            })}
                            onClick={() => setActiveTab("attachments")}
                          >
                            Attachments
                          </button>
                          <button
                            type="button"
                            className={classNames(detailsStyles.tabButton, {
                              [detailsStyles.activeTab]:
                                activeTab === "change-history",
                            })}
                            onClick={() => setActiveTab("change-history")}
                          >
                            Change History
                          </button>
                          <button
                            type="button"
                            className={classNames(detailsStyles.tabButton, {
                              [detailsStyles.activeTab]: activeTab === "comments",
                            })}
                            onClick={() => setActiveTab("comments")}
                          >
                            Comments
                          </button>
                        </div>
                      </div>

                      <div className={detailsStyles.tabPanel}>
                        {renderTabContent(activeTab, values)}
                      </div>
                    </div>

                    <div className={detailsStyles.sideColumn}>
                      <div className={detailsStyles.communicationCard}>
                        <div className={detailsStyles.communicationHeader}>
                          <div>
                            <div className={detailsStyles.communicationTitle}>
                              Communication Module
                            </div>
                            <div className={detailsStyles.communicationSubtitle}>
                              Add comments for this finding.
                            </div>
                          </div>
                        </div>
                        <Formik
                          initialValues={{ body: "", attachments: [] }}
                          onSubmit={handleUpdateSubmit}
                        >
                          {({ values: commValues, isSubmitting: commSubmitting, submitForm: submitComm }) => (
                            <div className={detailsStyles.communicationForm}>
                              {userEmail && (
                                <div className={detailsStyles.communicationUser}>
                                  {userEmail}
                                </div>
                              )}
                              <Field
                                name="body"
                                component={Textarea}
                                minHeight="90px"
                                className={detailsStyles.communicationTextarea}
                                placeholder="Add a comment..."
                              />
                              <Field
                                name="attachments"
                                component={FormikUpload}
                                greenIcon
                                className={detailsStyles.communicationUpload}
                                labelClassName={detailsStyles.communicationUploadLabel}
                                labelContent={
                                  <span className={detailsStyles.communicationUploadContent}>
                                    <Paperclip
                                      size={14}
                                      className={detailsStyles.communicationUploadIcon}
                                    />
                                    <span className={detailsStyles.communicationUploadText}>
                                      Attach files
                                    </span>
                                    <span className={detailsStyles.communicationUploadHint}>
                                      PDF, PNG, DOC
                                    </span>
                                  </span>
                                }
                              />
                              <Button
                                text={commSubmitting ? "Posting..." : "Post comment"}
                                className={detailsStyles.communicationSubmit}
                                icon={<Send size={14} />}
                                iconPosition="right"
                                type="button"
                                onClick={submitComm}
                                disabled={
                                  commSubmitting ||
                                  (!commValues.body.trim() &&
                                    (commValues.attachments || []).length === 0)
                                }
                              />
                            </div>
                          )}
                        </Formik>
                      </div>
                    </div>
                  </div>
                  <div className={styles.footerActions}>
                    <Button
                      text="Cancel"
                      type="button"
                      onClick={() => handleClose(dirty, resetForm)}
                      disabled={isSubmitting}
                      className={styles.cancelButton}
                    />
                    <Button
                      text={isSubmitting ? "Updating..." : "Update"}
                      type="button"
                      className={styles.updateButton}
                      onClick={submitForm}
                      disabled={isSubmitting || !dirty}
                    />
                  </div>
                </Form>
              </div>
            </Modal>
            {showConfirmationPopup && (
              <ConfirmationPopup
                message="Your changes won't be saved if you close this window."
                illustration={WarningIllustration}
                confirmText="Close Without Saving"
                onConfirm={() => handleConfirmClose(resetForm)}
                onCancel={handleCancelClose}
              />
            )}
          </>
        );
      }}
    </Formik>
  );
};

export default FindingsModal;
